import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/constants";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Loader from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Package, Plus, ClipboardCheck, Boxes, Wrench, Truck, ArrowLeftRight } from "lucide-react";
import { KpiStrip } from "@/components/Widget";
import { toast } from "sonner";
import { TYPES, TYPE_META, emptyItem } from "@/components/stock/constants";
import ItemFormDialog from "@/components/stock/ItemFormDialog";
import ItemDetailSheet from "@/components/stock/ItemDetailSheet";
import ReserveDialog from "@/components/stock/ReserveDialog";
import ReturnDialog from "@/components/stock/ReturnDialog";
import IncidentDialog from "@/components/stock/IncidentDialog";
import ImportDialog from "@/components/stock/ImportDialog";
import { ExportDialog } from "@/components/stock/ExportDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";
import InventoryPanel from "@/components/stock/InventoryPanel";
import MovementsPanel from "@/components/stock/MovementsPanel";
import CategoryManager from "@/components/stock/CategoryManager";
import { CategoryTag } from "@/lib/categoryColors";
import { StoredImg } from "@/components/stock/FileField";

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
function score(it, terms) {
  const name = norm(it.name);
  const other = norm([it.category, it.description, it.reference, it.sku].filter(Boolean).join(" "));
  let total = 0;
  for (const t of terms) {
    if (name.startsWith(t)) total += 100;
    else if (name.split(/[\s\-_/]+/).some((w) => w.startsWith(t))) total += 60;
    else if (name.includes(t)) total += 30;
    else if (other.includes(t)) total += 10;
    else return 0;
  }
  return total;
}

function FilterSelect({ testid, value, onChange, all, allLabel, options }) {
  const on = value !== all;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-9 w-auto min-w-[150px] gap-2 rounded-full border px-4 text-sm ${on ? "border-[#FF6A00]/60 bg-[#FF6A00]/10 text-white" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72 border-zinc-800 bg-zinc-950">
        <SelectItem value={all}>{allLabel}</SelectItem>
        {options.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${testid}-${k}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Stock() {
  const { org, user, refreshOrg } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [query, setQuery] = useState(() => sessionStorage.getItem("stock-search") || "");
  useEffect(() => { sessionStorage.setItem("stock-search", query); }, [query]);
  const location = useLocation();
  const warehouses = org?.warehouses || [];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("tous");
  const [whFilter, setWhFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const categories = org?.catalog_categories || [];
  const [delItemTarget, setDelItemTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [detail, setDetail] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [res, setRes] = useState({ event_id: "", start_date: today, end_date: today, quantity: 1 });
  const [returnOpen, setReturnOpen] = useState(false);
  const [activeRes, setActiveRes] = useState(null);
  const [ret, setRet] = useState({ condition: "ok", caution_action: "liberer", retained_amount: 0, notes: "" });
  const [packComp, setPackComp] = useState({ item_id: "", quantity: 1 });
  const [essComp, setEssComp] = useState({ item_id: "", quantity: 1 });
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [resyncTarget, setResyncTarget] = useState(null);
  const [invTasks, setInvTasks] = useState([]);
  const [invMembers, setInvMembers] = useState([]);
  const [countTask, setCountTask] = useState(null);
  const [countRows, setCountRows] = useState([]);
  const [newInvOpen, setNewInvOpen] = useState(false);
  const [newInvItems, setNewInvItems] = useState([]);
  const [newInvAssignee, setNewInvAssignee] = useState("");
  const [invCounts, setInvCounts] = useState([]);

  const load = () => api.get("/inventory").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  const loadTasks = () => api.get("/inventory-tasks").then(({ data }) => { setInvTasks(data.tasks || []); setInvMembers(data.members || []); }).catch(() => {});
  const loadCounts = () => api.get("/inventory-counts").then(({ data }) => setInvCounts(data || [])).catch(() => {});
  useEffect(() => { load(); loadTasks(); loadCounts(); api.get("/events").then(({ data }) => setEvents(data)); }, []);

  const updateQuantity = async (id, payload) => {
    try { await api.patch(`/inventory/${id}/quantity`, payload); toast.success("Quantité mise à jour"); openDetail(id); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const assignTask = async (tid, userId) => {
    try { await api.post(`/inventory-tasks/${tid}/assign`, { user_id: userId }); toast.success(userId ? "Inventaire assigné" : "Assignation retirée"); loadTasks(); }
    catch { toast.error("Erreur"); }
  };
  const completeTask = async (tid, counts) => {
    try { await api.post(`/inventory-tasks/${tid}/complete`, counts ? { counts } : {}); toast.success("Inventaire marqué comme fait"); setCountTask(null); loadTasks(); loadCounts(); load(); }
    catch { toast.error("Erreur"); }
  };
  const openCount = (t) => {
    const rows = [];
    (t.items || []).forEach((ti) => {
      const it = items.find((x) => x.id === ti.item_id);
      const locs = (it && it.locations) || [];
      if (locs.length > 0) locs.forEach((l) => rows.push({ item_id: ti.item_id, name: ti.name, location_id: l.location_id, counted: l.quantity || 0 }));
      else rows.push({ item_id: ti.item_id, name: ti.name, location_id: null, counted: (it && it.total_quantity) || 0 });
    });
    setCountRows(rows); setCountTask(t);
  };
  const toggleNewInvItem = (id) => setNewInvItems((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  const launchInventory = async () => {
    if (newInvItems.length === 0) return toast.error("Sélectionnez au moins un article");
    try {
      await api.post("/inventory-tasks", { item_ids: newInvItems, assigned_to: newInvAssignee || null });
      toast.success("Inventaire lancé");
      setNewInvOpen(false); setNewInvItems([]); setNewInvAssignee(""); loadTasks();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  useEffect(() => { setPage(1); }, [filter, whFilter, catFilter]);
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get("open");
    if (openId) openDetail(openId);
  }, [location.search]); // eslint-disable-line

  const fileToData = (file, cb) => { if (!file) return; const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };
  const addEssential = () => {
    if (!essComp.item_id) return toast.error("Choisissez un article");
    setForm((f) => ({ ...f, essentials: [...(f.essentials || []), { item_id: essComp.item_id, quantity: parseInt(essComp.quantity) || 1 }] }));
    setEssComp({ item_id: "", quantity: 1 });
  };
  const removeEssential = (idx) => setForm((f) => ({ ...f, essentials: (f.essentials || []).filter((_, i) => i !== idx) }));
  const startEdit = (d) => {
    setForm({ name: d.name || "", type: d.type || "materiel", category: d.category || "Général", description: d.description || "", price: d.price || 0, total_quantity: d.total_quantity || 1, deposit_amount: d.deposit_amount || 0, purchase_price: d.purchase_price || 0, location_id: d.location_id || "", locations: d.locations || [], components: d.components || [], essentials: d.essentials || [], custom_fields: d.custom_fields || [], image: d.image || "", images: d.images?.length ? d.images : (d.image ? [d.image] : []), presentation_image: d.presentation_image || "", show_presentation_in_quote: !!d.show_presentation_in_quote, supplier_id: d.supplier_id || "", supplier_cost: d.supplier_cost || 0 });
    setEditingId(d.id); setDetail(null); setOpen(true);
  };
  const addItem = async () => {
    if (!form.name) return toast.error("Nom requis");
    const req = (payload) => (editingId ? api.put(`/inventory/${editingId}`, payload) : api.post("/inventory", payload));
    if (form.type === "pack") {
      if ((form.components || []).length === 0) return toast.error("Ajoutez au moins un élément au pack");
      const total = (form.components || []).reduce((s, c) => { const ci = items.find((i) => i.id === c.item_id); return s + (ci ? ci.price : 0) * (c.quantity || 1); }, 0);
      const custom = parseFloat(form.price);
      const price = custom > 0 ? custom : total;
      try {
        await req({ name: form.name, type: "pack", category: form.category, description: form.description, price, total_quantity: parseInt(form.total_quantity) || 1, components: form.components, image: form.image, images: form.images || [], presentation_image: form.presentation_image, show_presentation_in_quote: form.show_presentation_in_quote });
      } catch (e) { return toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
      toast.success(editingId ? "Pack modifié" : "Pack ajouté"); setOpen(false); setForm(emptyItem); setEditingId(null); load(); return;
    }
    const isMat = form.type === "materiel";
    const cleanFields = (form.custom_fields || [])
      .filter((cf) => (cf.label || "").trim() && (cf.options || []).length)
      .map((cf) => ({ id: cf.id, label: cf.label.trim(), options: cf.options }));
    const cleanLocs = (form.locations || []).filter((l) => l.location_id && (parseInt(l.quantity) || 0) > 0).map((l) => ({ location_id: l.location_id, quantity: parseInt(l.quantity) || 0 }));
    const matTotal = cleanLocs.length ? cleanLocs.reduce((s, l) => s + l.quantity, 0) : (parseInt(form.total_quantity) || 1);
    try {
      await req({
        name: form.name, type: form.type, category: form.category, description: form.description,
        price: parseFloat(form.price) || 0,
        total_quantity: isMat ? matTotal : 0,
        deposit_amount: isMat ? (parseFloat(form.deposit_amount) || 0) : 0,
        purchase_price: isMat ? (parseFloat(form.purchase_price) || 0) : 0,
        location_id: isMat ? (cleanLocs[0]?.location_id || form.location_id || "") : "",
        locations: isMat ? cleanLocs : [],
        essentials: form.essentials || [], custom_fields: cleanFields, image: form.image, images: form.images || [], presentation_image: form.presentation_image, show_presentation_in_quote: form.show_presentation_in_quote,
        supplier_id: form.type === "location" ? (form.supplier_id || "") : "",
        supplier_cost: form.type === "location" ? (parseFloat(form.supplier_cost) || 0) : 0,
      });
    } catch (e) { return toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    const savedId = editingId;
    const fem = form.type === "location" || form.type === "prestation";
    toast.success(`${TYPES[form.type]} ${editingId ? "modifié" : "ajouté"}${fem ? "e" : ""}`);
    setOpen(false); setForm(emptyItem); setEditingId(null); load();
    if (savedId) {
      try {
        const { data } = await api.get(`/inventory/${savedId}/reservation-impact`);
        if (data?.count > 0) setResyncTarget({ id: savedId, count: data.count });
      } catch { /* impact check is best-effort */ }
    }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/inventory/${id}`);
    const [c, m] = await Promise.all([api.get(`/inventory/${id}/counts`).then((r) => r.data || []).catch(() => []), api.get(`/inventory/${id}/movements`).then((r) => r.data || []).catch(() => [])]);
    setDetail({ ...data, _counts: c, _moves: m });
  };
  const delItem = async (id) => { try { await api.delete(`/inventory/${id}`); setDetail(null); load(); toast.success("Article supprimé"); } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); } finally { setDelItemTarget(null); } };
  const doResync = async () => {
    if (!resyncTarget) return;
    try {
      const { data } = await api.post(`/inventory/${resyncTarget.id}/resync-reservations`);
      toast.success(`${data.synced} devis re-synchronisé(s)${data.conflicts?.length ? ` — stock insuffisant : ${data.conflicts.join(", ")}` : ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setResyncTarget(null); load(); }
  };

  const reserve = async () => {
    try {
      await api.post("/reservations", { item_id: detail.id, ...res, quantity: parseInt(res.quantity) || 1, event_id: res.event_id || null });
      toast.success("Matériel réservé");
      setReserveOpen(false); openDetail(detail.id); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setResStatus = async (rid, status) => { await api.patch(`/reservations/${rid}/status`, { status }); openDetail(detail.id); load(); };
  const delRes = async (rid) => { await api.delete(`/reservations/${rid}`); openDetail(detail.id); load(); };
  const openReturn = (r) => { setActiveRes(r); setRet({ condition: "ok", caution_action: "liberer", retained_amount: r.caution_amount || 0, notes: "" }); setReturnOpen(true); };
  const addComponent = () => {
    if (!packComp.item_id) return toast.error("Choisissez un article");
    setForm((f) => ({ ...f, components: [...(f.components || []), { item_id: packComp.item_id, quantity: parseInt(packComp.quantity) || 1 }] }));
    setPackComp({ item_id: "", quantity: 1 });
  };
  const addIncident = async (payload) => {
    try { await api.post(`/inventory/${detail.id}/incident`, payload); toast.success("Incident enregistré — stock mis à jour"); setIncidentOpen(false); openDetail(detail.id); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const resolveIncident = async (incId) => {
    try { await api.post(`/inventory/${detail.id}/incident/${incId}/resolve`); toast.success("Incident résolu — unité rétablie"); openDetail(detail.id); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };
  const doReturn = async () => {
    await api.post(`/reservations/${activeRes.id}/return`, { ...ret, retained_amount: parseFloat(ret.retained_amount) || 0 });
    toast.success("État des lieux enregistré");
    setReturnOpen(false); openDetail(detail.id); load();
  };

  const panel = filter === "inventaire" || filter === "mouvements";
  const allCategories = [...new Set([...categories, ...items.map((i) => i.category).filter(Boolean)])].sort((a, b) => a.localeCompare(b, "fr"));
  const terms = norm(query).split(/\s+/).filter(Boolean);
  const base = (filter === "tous" || panel ? items : items.filter((i) => (i.type || "materiel") === filter))
    .filter((i) => catFilter === "all" || (i.category || "") === catFilter)
    .filter((i) => whFilter === "all" || ((i.type || "materiel") === "materiel" && (((i.locations || []).some((l) => l.location_id === whFilter)) || (i.location_id || "") === whFilter)));
  const filtered = terms.length
    ? base.map((i) => [i, score(i, terms)]).filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1] || String(a[0].name).localeCompare(String(b[0].name))).map(([i]) => i)
    : base;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const pendingInv = invTasks.filter((t) => t.status !== "fait");

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Catalogue & Matériel</h1>
          <p className="mt-1 text-sm text-zinc-500">Matériel, prestations et forfaits — tout ce que vous proposez, réutilisable dans les devis.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <ExportDialog pendingCount={pendingInv.length} selection={{
          ids: filtered.map((i) => i.id),
          label: [query && `Recherche « ${query} »`, !panel && filter !== "tous" && TYPES[filter], catFilter !== "all" && `Catégorie ${catFilter}`,
            whFilter !== "all" && `Entrepôt ${(warehouses.find((w) => w.id === whFilter) || {}).name || ""}`].filter(Boolean).join(" · "),
        }} />
        <ImportDialog warehouses={warehouses} onDone={load} />
        <ItemFormDialog
          open={open}
          onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyItem); setEditingId(null); } }}
          form={form} setForm={setForm} editingId={editingId} items={items} onSave={addItem}
          essComp={essComp} setEssComp={setEssComp} packComp={packComp} setPackComp={setPackComp}
          addEssential={addEssential} removeEssential={removeEssential} addComponent={addComponent} fileToData={fileToData} warehouses={warehouses} categories={categories}
        />
        </div>
      </div>

      {!loading && (
        <KpiStrip testid="stock-kpis" items={[
          { icon: Package, label: "Articles au catalogue", value: items.length, tint: "#FF6A00", onClick: () => setFilter("tous"), testid: "stock-kpi-all", active: filter === "tous" },
          { icon: Boxes, label: "Matériel", value: items.filter((i) => (i.type || "materiel") === "materiel").length, tint: "#FFD500", onClick: () => setFilter("materiel"), testid: "stock-kpi-materiel", active: filter === "materiel" },
          { icon: Truck, label: "Locations prestataires", value: items.filter((i) => i.type === "location").length, tint: "#60a5fa", onClick: () => setFilter("location"), testid: "stock-kpi-location", active: filter === "location" },
          { icon: Wrench, label: "Inventaires à faire", value: pendingInv.length, tint: "#34d399", onClick: () => setFilter("inventaire"), testid: "stock-kpi-inventaire", active: filter === "inventaire" },
        ]} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-zinc-800 bg-zinc-900 p-1" data-testid="stock-view-tabs">
          {[["catalogue", "Catalogue", Package], ["inventaire", "Inventaire à faire", ClipboardCheck], ["mouvements", "Mouvements", ArrowLeftRight]].map(([k, l, I]) => {
            const on = k === "catalogue" ? !panel : filter === k;
            return (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k === "catalogue" ? "tous" : k)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${on ? "bg-[#FF6A00] text-white" : "text-zinc-400 hover:text-white"}`}>
                <I className="h-4 w-4" /><span className={k === "catalogue" ? "" : "hidden sm:inline"}>{l}</span>
                {k === "inventaire" && pendingInv.length > 0 && <span className="rounded-full bg-[#FFD500] px-1.5 text-xs font-bold text-black">{pendingInv.length}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {!panel && (
      <div className="flex flex-wrap items-center gap-2" data-testid="stock-filters">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input data-testid="stock-search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Rechercher un article…"
              className="h-9 w-full rounded-full border border-zinc-800 bg-zinc-900 pl-9 pr-9 text-sm text-white placeholder:text-zinc-500 focus:border-[#FF6A00]/60 focus:outline-none" />
            {query && (
              <button data-testid="stock-search-clear" onClick={() => { setQuery(""); setPage(1); }} title="Vider la recherche"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-800 hover:text-white"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
          <FilterSelect testid="stock-type-filter" value={filter} onChange={setFilter} all="tous" allLabel="Tous les types" options={Object.keys(TYPES).map((k) => [k, { materiel: "Matériel", prestation: "Prestations", forfait: "Forfaits", pack: "Packs", location: "Locations" }[k]])} />
          {allCategories.length > 0 && <FilterSelect testid="stock-category-filter" value={catFilter} onChange={setCatFilter} all="all" allLabel="Toutes les catégories" options={allCategories.map((c) => [c, c])} />}
          {isAdmin && <CategoryManager onChanged={(src, dst, removed) => {
            load();
            if (org) refreshOrg({ ...org, catalog_categories: [...new Set([...(org.catalog_categories || []).filter((c) => !(removed && c === src)).map((c) => (c === src ? dst : c)), ...(dst ? [dst] : [])])] });
            if (catFilter === src) setCatFilter(dst || "all");
          }} />}
          {warehouses.length > 0 && <FilterSelect testid="stock-warehouse-filter" value={whFilter} onChange={setWhFilter} all="all" allLabel="Tous les entrepôts" options={warehouses.map((w) => [w.id, w.name])} />}
          {(filter !== "tous" || catFilter !== "all" || whFilter !== "all" || query) && (
            <button data-testid="stock-filters-reset" onClick={() => { setFilter("tous"); setCatFilter("all"); setWhFilter("all"); setQuery(""); }} className="px-2 text-xs text-zinc-500 transition-colors hover:text-[#FF6A00]">Réinitialiser</button>
          )}
          <span data-testid="stock-search-count" className="ml-auto text-xs text-zinc-500">{filtered.length} article{filtered.length > 1 ? "s" : ""}</span>
      </div>
      )}

      {filter === "mouvements" ? (
        <MovementsPanel items={items} warehouses={warehouses} />
      ) : filter === "inventaire" ? (
        <InventoryPanel tasks={invTasks} members={invMembers} counts={invCounts} warehouses={warehouses}
          onLaunch={() => { setNewInvItems([]); setNewInvAssignee(""); setNewInvOpen(true); }} onAssign={assignTask} onCount={openCount} isAdmin={isAdmin} />
      ) : loading ? (
        <Loader label="Chargement du catalogue…" />
      ) : filtered.length === 0 ? (
        <div data-testid="stock-empty" className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-16 text-center">
          <Package className="mx-auto h-12 w-12 text-zinc-700" />
          {items.length > 0 ? (
            <>
              <p className="mt-4 font-medium text-white">{query ? `Aucun résultat pour « ${query} »` : "Aucun article pour ce filtre"}</p>
              <p className="mt-1 text-sm text-zinc-500">Essayez un autre mot ou réaffichez tout le catalogue.</p>
              <Button data-testid="stock-empty-reset" onClick={() => { setQuery(""); setFilter("tous"); setWhFilter("all"); setCatFilter("all"); setPage(1); }} className="mt-5 rounded-full bg-zinc-800 text-white hover:bg-zinc-700">Tout réafficher</Button>
            </>
          ) : (
            <>
              <p className="mt-4 font-medium text-white">Rien dans votre catalogue</p>
              <p className="mt-1 text-sm text-zinc-500">Ajoutez du matériel, une prestation ou un forfait.</p>
              <Button onClick={() => setOpen(true)} className="mt-5 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paged.map((it) => {
            const type = it.type || "materiel";
            const meta = TYPE_META[type];
            const Icon = meta.icon;
            return (
              <button key={it.id} data-testid={`item-card-${it.id}`} onClick={() => openDetail(it.id)}
                className="group rounded-2xl border border-zinc-800 bg-zinc-900 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-zinc-700">
                <div className="flex items-start justify-between">
                  {it.image ? <StoredImg value={it.image} data-testid={`item-card-photo-${it.id}`} className="h-14 w-14 rounded-xl border border-zinc-800 object-cover" />
                    : <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${meta.cls}`}><Icon className="h-5 w-5" strokeWidth={1.6} /></div>}
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${meta.badge}`}>{TYPES[type]}</span>
                </div>
                <p className="mt-3 font-medium text-white">{it.name}</p>
                <CategoryTag org={org} name={it.category} testid={`item-category-${it.id}`} className="mt-0.5" />
                {type === "materiel" && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Dispo aujourd'hui</span>
                    <span className={`font-semibold ${it.available_today <= 0 ? "text-red-400" : "text-emerald-400"}`}>{it.available_today}/{it.total_quantity}</span>
                  </div>
                )}
                {type === "pack" && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-zinc-500">{(it.components || []).length} élément(s) inclus</span>
                    <span className="font-semibold text-purple-300">×{it.total_quantity} pack{it.total_quantity > 1 ? "s" : ""}</span>
                  </div>
                )}
                {type === "pack" && it.packs_possible != null && (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Réalisables (stock)</span>
                    <span className={`font-semibold ${it.available_today <= 0 ? "text-red-400" : "text-emerald-400"}`}>{it.available_today}</span>
                  </div>
                )}
                {type === "location" && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Loué chez</span>
                    <span data-testid={`item-supplier-${it.id}`} className="truncate pl-2 font-medium text-[#E6B800]">{it.supplier_name || "—"}</span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{type === "materiel" || type === "location" ? "Location" : "Prix"}</span>
                  <span className="font-semibold text-white">{eur(it.price)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!panel && filtered.length > PAGE_SIZE && (
        <div data-testid="stock-pagination" className="flex items-center justify-center gap-3 pt-2">
          <Button data-testid="stock-prev" variant="outline" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-full border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40">Précédent</Button>
          <span className="text-sm text-zinc-400">Page {curPage} / {totalPages} · {filtered.length} article(s)</span>
          <Button data-testid="stock-next" variant="outline" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-full border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40">Suivant</Button>
        </div>
      )}

      <ItemDetailSheet
        detail={detail} onClose={() => setDetail(null)} items={items} onOpenItem={openDetail} warehouses={warehouses}
        onEdit={startEdit} onDelete={(it) => setDelItemTarget(it)} onReserveOpen={() => setReserveOpen(true)}
        onSetResStatus={setResStatus} onDelRes={delRes} onOpenReturn={openReturn}
        onAddIncident={() => setIncidentOpen(true)} onResolveIncident={resolveIncident}
        onUpdateQuantity={updateQuantity} counts={(detail && detail._counts) || []} moves={(detail && detail._moves) || []}
      />

      <Dialog open={!!countTask} onOpenChange={(o) => !o && setCountTask(null)}>        <DialogContent data-testid="inv-count-dialog" className="border-zinc-800 bg-zinc-950 text-white">
          <DialogHeader><DialogTitle>Comptage de l'inventaire</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">Saisissez la quantité réellement comptée. L'écart avec le stock théorique sera enregistré dans l'historique.</p>
          <div className="max-h-[50vh] space-y-2 overflow-auto py-1">
            {countRows.map((r, i) => { const w = warehouses.find((x) => x.id === r.location_id); return (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-sm text-zinc-300">{r.name}{r.location_id ? ` · ${w ? w.name : "entrepôt"}` : ""}</span>
                <input data-testid={`inv-count-input-${i}`} type="number" min="0" value={r.counted} onChange={(e) => setCountRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, counted: e.target.value } : x)))} className="w-24 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-right text-sm text-white" />
              </div>
            ); })}
          </div>
          <Button data-testid="inv-count-submit" onClick={() => completeTask(countTask.id, countRows.map((r) => ({ item_id: r.item_id, location_id: r.location_id, counted: parseInt(r.counted) || 0 })))} className="mt-2 w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Valider le comptage</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={newInvOpen} onOpenChange={setNewInvOpen}>
        <DialogContent data-testid="new-inv-dialog" className="border-zinc-800 bg-zinc-950 text-white">
          <DialogHeader><DialogTitle>Lancer un inventaire</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">Choisissez les articles de matériel à recompter. Vous pourrez l'assigner à un membre de l'équipe.</p>
          <div className="max-h-[45vh] space-y-1.5 overflow-auto py-1">
            {items.filter((i) => (i.type || "materiel") === "materiel").length === 0 ? (
              <p className="py-6 text-center text-sm text-zinc-500">Aucun matériel dans le catalogue.</p>
            ) : items.filter((i) => (i.type || "materiel") === "materiel").map((it) => (
              <label key={it.id} data-testid={`new-inv-item-${it.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm hover:border-zinc-700">
                <input type="checkbox" checked={newInvItems.includes(it.id)} onChange={() => toggleNewInvItem(it.id)} className="h-4 w-4 accent-[#FF6A00]" />
                <span className="flex-1 text-zinc-200">{it.name}</span>
                <span className="text-xs text-zinc-500">{it.category}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="text-xs text-zinc-400">Assigner à (optionnel)</label>
            <select data-testid="new-inv-assignee" value={newInvAssignee} onChange={(e) => setNewInvAssignee(e.target.value)} className="mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300">
              <option value="">Personne pour l'instant</option>
              {invMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <Button data-testid="new-inv-submit" onClick={launchInventory} className="mt-2 w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Lancer l'inventaire ({newInvItems.length})</Button>
        </DialogContent>
      </Dialog>

      <IncidentDialog open={incidentOpen} onOpenChange={setIncidentOpen} detail={detail} warehouses={warehouses} onConfirm={addIncident} />

      <ReserveDialog open={reserveOpen} onOpenChange={setReserveOpen} detail={detail} res={res} setRes={setRes} events={events} onConfirm={reserve} />

      <ReturnDialog open={returnOpen} onOpenChange={setReturnOpen} activeRes={activeRes} ret={ret} setRet={setRet} onConfirm={doReturn} />

      <ConfirmDialog
        open={!!delItemTarget} onOpenChange={(o) => !o && setDelItemTarget(null)}
        title="Supprimer cet article ?"
        description={delItemTarget ? `« ${delItemTarget.name} » sera définitivement supprimé du catalogue. Cette action est irréversible.` : ""}
        onConfirm={() => delItem(delItemTarget.id)} testid="del-item-dialog"
      />

      <ConfirmDialog
        open={!!resyncTarget} onOpenChange={(o) => !o && setResyncTarget(null)}
        title="Re-synchroniser le stock des devis ?" destructive={false} confirmLabel="Re-synchroniser"
        description={resyncTarget ? `Cet article figure dans ${resyncTarget.count} devis confirmé(s). La re-synchronisation met à jour leurs réservations pour prendre en compte les nouveaux indispensables et les quantités à jour.` : ""}
        onConfirm={doResync} testid="resync-dialog"
      />
    </div>
  );
}
